import { type WidgetSpec, widgetSpecSchema } from "@growth/schema";
import { mergeAttributes, Node } from "@tiptap/core";

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    widget: {
      /** Insert a chart/widget. Returns false when the spec is invalid. */
      insertWidget: (spec: WidgetSpec) => ReturnType;
    };
  }
}

/** Parse a stored spec, or null if it no longer validates. */
export const parseWidgetSpec = (value: unknown): WidgetSpec | null => {
  let candidate = value;
  if (typeof value === "string") {
    try {
      candidate = JSON.parse(value);
    } catch {
      return null;
    }
  }
  const result = widgetSpecSchema.safeParse(candidate);
  return result.success ? result.data : null;
};

/**
 * A chart or widget block. The document stores only the validated spec; the HTML here is a slot
 * (`<figure data-widget="…">`) that the publish step replaces with server-rendered markup from
 * libs/widgets. The admin editor swaps in a React node view for the live preview.
 */
export const WidgetNode = Node.create({
  name: "widget",
  group: "block",
  atom: true,
  draggable: true,

  addAttributes() {
    return { spec: { default: null as WidgetSpec | null } };
  },

  parseHTML() {
    return [
      {
        tag: "figure[data-widget]",
        getAttrs: (element) => {
          const spec = parseWidgetSpec((element as HTMLElement).getAttribute("data-widget"));
          return spec ? { spec } : false;
        },
      },
    ];
  },

  renderHTML({ node }) {
    return [
      "figure",
      mergeAttributes({
        "data-widget": JSON.stringify(node.attrs["spec"]),
        class: "cms-widget-slot",
      }),
    ];
  },

  addCommands() {
    return {
      insertWidget:
        (spec) =>
        ({ commands }) =>
          parseWidgetSpec(spec)
            ? commands.insertContent({ type: this.name, attrs: { spec } })
            : false,
    };
  },
});
